import React, { useMemo, useState } from 'react';
import { motion } from 'framer-motion';
import { format } from 'date-fns';
import { XIcon } from 'lucide-react';
import { useStudio } from '../../contexts/StudioContext';
import { vehicleSizes } from '../../data/schedule';
import { PaymentMethod, VehicleSizeId } from '../../types/booking';
import { depositFor, priceFor, slotsForDate } from '../../utils/availability';
import { duration, euro, longDate } from '../../utils/format';
type Props = {
  date: string;
  onClose: () => void;
  onCreated: (reference: string) => void;
};
export function NewBookingDialog({
  date,
  onClose,
  onCreated
}: Props) {
  const {
    services,
    extras,
    settings,
    bookings,
    createBooking
  } = useStudio();
  const [serviceId, setServiceId] = useState(services[0]?.id ?? '');
  const [extraIds, setExtraIds] = useState<string[]>([]);
  const [sizeId, setSizeId] = useState<VehicleSizeId>('sedan');
  const [day, setDay] = useState(date);
  const [time, setTime] = useState<string | null>(null);
  const [method, setMethod] = useState<PaymentMethod>('studio');
  const [customer, setCustomer] = useState({
    name: '',
    email: '',
    phone: '',
    car: '',
    plate: '',
    notes: ''
  });
  const [error, setError] = useState<string | null>(null);
  const service = services.find(s => s.id === serviceId) ?? null;
  const chosen = extras.filter(e => extraIds.includes(e.id));
  const size = vehicleSizes.find(v => v.id === sizeId) ?? null;
  const price = priceFor(service, chosen, size);
  const slots = useMemo(() => slotsForDate(day, price.minutes, bookings, settings), [day, price.minutes, bookings, settings]);
  const field = 'mt-1.5 w-full rounded-xl border border-edge bg-panel px-3.5 py-2.5 text-sm text-chalk placeholder:text-dim/60 focus:border-ember';
  const submit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!serviceId || !time) {
      setError('Kies een behandeling en een tijd.');
      return;
    }
    if (!customer.name.trim() || !customer.phone.trim()) {
      setError('Naam en telefoonnummer zijn verplicht.');
      return;
    }
    const booking = createBooking({
      serviceId,
      extraIds,
      vehicleSizeId: sizeId,
      date: day,
      time,
      paymentMethod: method,
      customer,
      source: 'admin'
    });
    onCreated(booking.reference);
    onClose();
  };
  return <div className="fixed inset-0 z-[60] flex items-end justify-center lg:items-stretch lg:justify-end" data-id="element-247" data-mp="32oipy8of3ljt">
      <button type="button" aria-label="Sluiten" onClick={onClose} className="absolute inset-0 bg-void/70" data-id="element-248" data-mp="2owvrq3ax02ye" />
      <motion.form onSubmit={submit} initial={{
      y: 40,
      opacity: 0
    }} animate={{
      y: 0,
      opacity: 1
    }} transition={{
      duration: 0.24,
      ease: [0.23, 1, 0.32, 1]
    }} className="relative flex max-h-[92vh] w-full max-w-md flex-col overflow-y-auto rounded-t-3xl border border-edge bg-carbon lg:h-full lg:max-h-none lg:rounded-none lg:border-0 lg:border-l" aria-label="Nieuwe afspraak inplannen" data-id="element-249" data-mp="2tri52c0k45l4">
        <span className="mx-auto mt-2.5 h-1 w-10 shrink-0 rounded-full bg-edge lg:hidden" aria-hidden="true" data-id="element-250" data-mp="1rcm4wytjqz9i" />
        <div className="flex items-start justify-between gap-4 border-b border-edge px-6 py-5" data-id="element-251" data-mp="1mg5hk6u68oxh">
          <div data-id="element-252" data-mp="20fdj36dt494u">
            <h2 className="font-display text-xl font-extrabold tracking-tight text-chalk" data-id="element-253" data-mp="20bzio3opea01">Nieuwe afspraak</h2>
            <p className="mt-1 text-sm capitalize text-dim" data-id="element-254" data-mp="1vhbxadozxvpo">{longDate(day)}</p>
          </div>
          <button type="button" onClick={onClose} aria-label="Sluiten" className="flex h-9 w-9 items-center justify-center rounded-full border border-edge text-dim transition-colors duration-200 ease-smooth hover:text-chalk" data-id="element-255" data-mp="2a9x46jf6hsoy">
            <XIcon className="h-4 w-4" aria-hidden="true" data-id="element-256" data-mp="2f52zg8uh0jpp" />
          </button>
        </div>

        <div className="space-y-5 px-6 py-5" data-id="element-257" data-mp="2t30xu4sec5jk">
          <div data-id="element-258" data-mp="2o46i5bpq6x3o">
            <label htmlFor="nb-service" className="text-sm font-medium text-chalk" data-id="element-259" data-mp="2o4ppjyhr233g">
              Behandeling
            </label>
            <select id="nb-service" value={serviceId} onChange={e => {
            setServiceId(e.target.value);
            setTime(null);
          }} className={field} data-id="element-260" data-mp="385ivzgvh9hxs">
              {services.map(s => <option key={s.id} value={s.id} data-id="element-261" data-mp="3lwo1v0pfv6gk">
                  {s.name} — {euro(s.price)}
                </option>)}
            </select>
          </div>

          <div data-id="element-262" data-mp="2t7ql520vlhpx">
            <span className="text-sm font-medium text-chalk" data-id="element-263" data-mp="2t9a1esmj7srp">Extra's</span>
            <div className="mt-2 space-y-2" data-id="element-264" data-mp="2y7btu45ul4fu">
              {extras.map(extra => <label key={extra.id} className="flex items-center justify-between gap-3 rounded-xl border border-edge bg-panel px-3.5 py-2.5 text-sm text-chalk" data-id="element-265" data-mp="336elmmwthe5v">
                  <span className="flex items-center gap-3" data-id="element-266" data-mp="ifnygd6hfjz5">
                    <input type="checkbox" checked={extraIds.includes(extra.id)} onChange={e => {
                  setExtraIds(prev => e.target.checked ? [...prev, extra.id] : prev.filter(x => x !== extra.id));
                  setTime(null);
                }} className="h-4 w-4 accent-[#ff6a2b]" data-id="element-267" data-mp="22yi0bopgc2fx" />
                    {extra.name}
                  </span>
                  <span className="text-dim" data-id="element-268" data-mp="1f7vrhv7mie4v">+{euro(extra.price)}</span>
                </label>)}
            </div>
          </div>

          <div data-id="element-269" data-mp="1abx0dzuwui0r">
            <label htmlFor="nb-size" className="text-sm font-medium text-chalk" data-id="element-270" data-mp="15ar5bvds2io2">
              Voertuigklasse
            </label>
            <select id="nb-size" value={sizeId} onChange={e => setSizeId(e.target.value as VehicleSizeId)} className={field} data-id="element-271" data-mp="34o3m3slfkh7b">
              {vehicleSizes.map(v => <option key={v.id} value={v.id} data-id="element-272" data-mp="xy9ws7jhai8m">
                  {v.label} {v.surchargePct > 0 ? `(+${v.surchargePct}%)` : ''}
                </option>)}
            </select>
          </div>

          <div data-id="element-273" data-mp="nzc54vqra8le">
            <label htmlFor="nb-date" className="text-sm font-medium text-chalk" data-id="element-274" data-mp="t0hymvs8pzvz">
              Datum
            </label>
            <input id="nb-date" type="date" value={day} min={format(new Date(), 'yyyy-MM-dd')} onChange={e => {
            setDay(e.target.value);
            setTime(null);
          }} className={field} data-id="element-275" data-mp="1gpkqsxki3b4q" />
          </div>

          <div data-id="element-276" data-mp="1bpon6zfptlpp">
            <span className="text-sm font-medium text-chalk" data-id="element-277" data-mp="1bp6h7b393t4l">
              Tijd <span className="text-dim" data-id="element-278" data-mp="3sek73z4k6sg0">({duration(price.minutes)})</span>
            </span>
            {slots.length > 0 ? <div className="mt-2 grid grid-cols-3 gap-2" data-id="element-279" data-mp="d5mzgnmbchg">
                {slots.map(slot => <button key={slot.time} type="button" onClick={() => setTime(slot.time)} className={`rounded-xl border px-2 py-2.5 text-sm transition-colors duration-200 ease-smooth ${time === slot.time ? 'border-ember bg-ember text-void' : slot.available ? 'border-edge bg-panel text-chalk hover:border-ember/50' : 'border-edge/60 bg-panel text-dim/50'}`} data-id="element-280" data-mp="3ovuzjmrkmljf">
                    {slot.time}
                  </button>)}
              </div> : <p className="mt-2 rounded-xl border border-dashed border-edge px-3.5 py-3 text-sm text-dim" data-id="element-281" data-mp="3b4gbh1it3u5i">
                Werkplaats gesloten op deze dag — kies een andere datum.
              </p>}
            <p className="mt-2 text-xs text-dim" data-id="element-282" data-mp="1wykcwttngao9">
              Als beheerder kunt u ook een bezette of te vroege tijd kiezen (dubbelboeking).
            </p>
          </div>

          <div className="grid grid-cols-2 gap-3" data-id="element-283" data-mp="21zhvv8mhh8qt">
            <div data-id="element-284" data-mp="26w8hebh24wjm">
              <label htmlFor="nb-name" className="text-sm font-medium text-chalk" data-id="element-285" data-mp="26voyarfo8240">
                Naam
              </label>
              <input id="nb-name" value={customer.name} onChange={e => setCustomer(c => ({
              ...c,
              name: e.target.value
            }))} className={field} placeholder="Klantnaam" data-id="element-286" data-mp="2avz7ecyrf8n3" />
            </div>
            <div data-id="element-287" data-mp="2pnhsgp6h7tdb">
              <label htmlFor="nb-phone" className="text-sm font-medium text-chalk" data-id="element-288" data-mp="2pp2nefwpdebp">
                Telefoon
              </label>
              <input id="nb-phone" value={customer.phone} onChange={e => setCustomer(c => ({
              ...c,
              phone: e.target.value
            }))} className={field} placeholder="06 …" data-id="element-289" data-mp="2yo6aduyzqdvh" />
            </div>
          </div>

          <div className="grid grid-cols-2 gap-3" data-id="element-290" data-mp="n0kp94t9w8nb">
            <div data-id="element-291" data-mp="myzqt43u85k6">
              <label htmlFor="nb-car" className="text-sm font-medium text-chalk" data-id="element-292" data-mp="n2nlw7oxl010">
                Voertuig
              </label>
              <input id="nb-car" value={customer.car} onChange={e => setCustomer(c => ({
              ...c,
              car: e.target.value
            }))} className={field} placeholder="VW Golf" data-id="element-293" data-mp="e05z9s5d65dh" />
            </div>
            <div data-id="element-294" data-mp="16qo2ct6386qw">
              <label htmlFor="nb-plate" className="text-sm font-medium text-chalk" data-id="element-295" data-mp="11xblj57nh48p">
                Kenteken
              </label>
              <input id="nb-plate" value={customer.plate} onChange={e => setCustomer(c => ({
              ...c,
              plate: e.target.value.toUpperCase()
            }))} className={field} placeholder="XX-000-X" data-id="element-296" data-mp="wxpan2fao032" />
            </div>
          </div>

          <div data-id="element-297" data-mp="35smjabqypxbf">
            <label htmlFor="nb-email" className="text-sm font-medium text-chalk" data-id="element-298" data-mp="35u75ne34i9y7">
              E-mail
            </label>
            <input id="nb-email" type="email" value={customer.email} onChange={e => setCustomer(c => ({
            ...c,
            email: e.target.value
          }))} className={field} placeholder="naam@voorbeeld.nl" data-id="element-299" data-mp="3esss3kbpu949" />
          </div>

          <div data-id="element-300" data-mp="3js5fc66i1i7d">
            <label htmlFor="nb-notes" className="text-sm font-medium text-chalk" data-id="element-301" data-mp="3jsnmvyym3iul">
              Opmerkingen
            </label>
            <textarea id="nb-notes" rows={3} value={customer.notes} onChange={e => setCustomer(c => ({
            ...c,
            notes: e.target.value
          }))} className={`${field} resize-none`} data-id="element-302" data-mp="2aummk1vsp6w" />
          </div>

          <div data-id="element-303" data-mp="6dr0jxgh7x4i">
            <span className="text-sm font-medium text-chalk" data-id="element-304" data-mp="6eafr6eqs7eu">Betaling</span>
            <div className="mt-2 grid grid-cols-2 gap-2" data-id="element-305" data-mp="pyhq546kn9vv">
              {(['studio', 'deposit'] as const).map(option => <button key={option} type="button" onClick={() => setMethod(option)} className={`rounded-xl border px-3 py-2.5 text-sm transition-colors duration-200 ease-smooth ${method === option ? 'border-ember bg-ember text-void' : 'border-edge bg-panel text-chalk hover:border-ember/50'}`} data-id="element-306" data-mp="uxc7e1ow4qdv">
                  {option === 'studio' ? 'In werkplaats' : `Aanbetaald ${euro(depositFor(price.amount, settings))}`}
                </button>)}
            </div>
          </div>
        </div>

        {error && <p className="px-6 pb-2 text-sm text-red-400" data-id="element-307" data-mp="3kfurd3oz5mj6">{error}</p>}

        <div className="safe-bottom mt-auto flex items-center justify-between gap-4 border-t border-edge bg-carbon px-6 py-4" data-id="element-308" data-mp="3pdmtsrn8wr1m">
          <span data-id="element-309" data-mp="1uypqkch90dyy">
            <span className="block text-[11px] uppercase tracking-[0.14em] text-dim" data-id="element-310" data-mp="1uz8xky5d1hy7">Totaal</span>
            <span className="font-display text-xl font-extrabold text-ember" data-id="element-311" data-mp="1q6640q0l28i0">{euro(price.amount)}</span>
          </span>
          <button type="submit" className="rounded-full bg-ember px-6 py-3.5 text-sm font-semibold text-void transition-[background-color,box-shadow] duration-200 ease-smooth hover:bg-white hover:glow" data-id="element-312" data-mp="1l5s3ru5842uj">
            Afspraak inplannen
          </button>
        </div>
      </motion.form>
    </div>;
}